import { brandTitleByKey } from '@prismamedia/one-brandkey';
import { BrandKey, TagType } from '../__generated__/queries-web';
import { InternalLinkType } from '../components/InternalLinkModal/InternalLink';
import { MEDIA_TYPES } from '../components/MediaInput/constants';
import { PluginType } from '../pages/ArticleEdit/templates/utils';

const BASE_RICH_FIELDS = [
  'publishedAt',
  'unpublishAt',
  'title',
  'partnerTitle',
  'discoverTitle',
  'medias',
  'lightDraft',
  'richDraft',
  'firstCategory',
  'tags',
  'slug',
];

const RICH_MEDIA_TYPES = [
  MEDIA_TYPES.IMAGE,
  MEDIA_TYPES.EMBED,
  MEDIA_TYPES.ARTICLE_VIDEO,
  MEDIA_TYPES.SLIDESHOW,
  MEDIA_TYPES.PODCAST,
  MEDIA_TYPES.FOTOWEB,
];

interface BrandOptions {
  affiliate?: boolean;
  isCerise?: boolean;
  disabledForCerise?: boolean;
  displayIATitle?: boolean;
  draft?: {
    richFields: string[];
    plugins: PluginType[];
  };
  editorialAlertAllowed?: boolean;
  internalLinks?: InternalLinkType[];
  medias?: {
    richTypes: MEDIA_TYPES[];
  };
  mediaProgramsAllowed?: boolean;
  personTagsAllowed?: boolean;
  pushPlatforms?: ('android' | 'ios')[];
  setInitialTitleAllowed?: boolean;
  tagProviders: TagType[];
  useFotoweb?: boolean;
  enableNLFlash?: boolean;
  isRecipe?: boolean;
  enableAffilizPlugin?: boolean;
}

const brandsOptions: Partial<Record<BrandKey, BrandOptions>> & {
  DEFAULT: BrandOptions;
} = {
  BIN: {
    draft: {
      richFields: [...BASE_RICH_FIELDS, 'authors', 'source'],
      plugins: [PluginType.Media, PluginType.Image, PluginType.Multimedia],
    },
    editorialAlertAllowed: true,
    medias: {
      richTypes: [
        MEDIA_TYPES.IMAGE,
        MEDIA_TYPES.EMBED,
        MEDIA_TYPES.ARTICLE_VIDEO,
        MEDIA_TYPES.FOTOWEB,
      ],
    },
    internalLinks: [InternalLinkType.ARTICLE],
    pushPlatforms: ['ios', 'android'],
    tagProviders: [TagType.Tag, TagType.Person],
    useFotoweb: true,
  },
  BONE: {
    tagProviders: [TagType.Tag],
  },
  CAC: {
    affiliate: true,
    draft: {
      richFields: [...BASE_RICH_FIELDS, 'authors', 'event', 'source', 'guides'],
      plugins: [
        PluginType.Media,
        PluginType.Image,
        PluginType.Multimedia,
        PluginType.PageBreak,
        PluginType.Table,
        PluginType.C2C,
        PluginType.Frame,
      ],
    },
    editorialAlertAllowed: true,
    medias: {
      richTypes: RICH_MEDIA_TYPES,
    },
    internalLinks: [InternalLinkType.ARTICLE, InternalLinkType.RECIPE],
    personTagsAllowed: true,
    pushPlatforms: ['ios', 'android'],
    tagProviders: [TagType.Tag, TagType.Person],
    useFotoweb: true,
    enableNLFlash: true,
    isRecipe: true,
    enableAffilizPlugin: true,
  },
  CAM: {
    draft: {
      richFields: [...BASE_RICH_FIELDS, 'authors', 'source'],
      plugins: [
        PluginType.Media,
        PluginType.Image,
        PluginType.Multimedia,
        PluginType.Frame,
        PluginType.C2C,
        PluginType.Table,
      ],
    },
    editorialAlertAllowed: true,
    medias: {
      richTypes: RICH_MEDIA_TYPES,
    },
    pushPlatforms: ['ios', 'android'],
    setInitialTitleAllowed: true,
    tagProviders: [TagType.Tag, TagType.Person],
    useFotoweb: true,
    enableNLFlash: true,
  },
  CTM: {
    draft: {
      richFields: [...BASE_RICH_FIELDS, 'authors', 'source'],
      plugins: [
        PluginType.Media,
        PluginType.Image,
        PluginType.Multimedia,
        PluginType.Frame,
        PluginType.C2C,
        PluginType.Table,
      ],
    },
    editorialAlertAllowed: true,
    medias: {
      richTypes: RICH_MEDIA_TYPES,
    },
    setInitialTitleAllowed: true,
    tagProviders: [TagType.Tag, TagType.Person],
    useFotoweb: true,
  },
  CAP: {
    affiliate: true,
    draft: {
      richFields: [...BASE_RICH_FIELDS, 'authors', 'event', 'source'],
      plugins: [
        PluginType.Media,
        PluginType.Image,
        PluginType.Multimedia,
        PluginType.PageBreak,
        PluginType.C2C,
        PluginType.Frame,
        PluginType.Table,
      ],
    },
    editorialAlertAllowed: true,
    medias: {
      richTypes: RICH_MEDIA_TYPES,
    },
    internalLinks: [InternalLinkType.ARTICLE],
    personTagsAllowed: true,
    pushPlatforms: ['ios', 'android'],
    tagProviders: [TagType.Tag, TagType.Person, TagType.Company],
    useFotoweb: true,
    enableNLFlash: true,
    enableAffilizPlugin: true,
  },
  DEGTS: {
    isCerise: true,
    draft: {
      richFields: [...BASE_RICH_FIELDS, 'authors', 'source'],
      plugins: [
        PluginType.Media,
        PluginType.Image,
        PluginType.Multimedia,
        PluginType.Frame,
        PluginType.C2C,
      ],
    },
    editorialAlertAllowed: true,
    medias: {
      richTypes: [
        MEDIA_TYPES.IMAGE,
        MEDIA_TYPES.EMBED,
        MEDIA_TYPES.ARTICLE_VIDEO,
        MEDIA_TYPES.FOTOWEB,
      ],
    },
    internalLinks: [InternalLinkType.ARTICLE],
    tagProviders: [TagType.Tag],
    useFotoweb: true,
  },
  DEOMM: {
    isCerise: true,
    draft: {
      richFields: [...BASE_RICH_FIELDS, 'authors', 'source'],
      plugins: [
        PluginType.Media,
        PluginType.Image,
        PluginType.Multimedia,
        PluginType.Frame,
        PluginType.C2C,
      ],
    },
    editorialAlertAllowed: true,
    medias: {
      richTypes: [
        MEDIA_TYPES.IMAGE,
        MEDIA_TYPES.EMBED,
        MEDIA_TYPES.ARTICLE_VIDEO,
        MEDIA_TYPES.FOTOWEB,
      ],
    },
    internalLinks: [InternalLinkType.ARTICLE],
    tagProviders: [TagType.Tag],
    useFotoweb: true,
  },
  FAC: {
    affiliate: true,
    draft: {
      richFields: [...BASE_RICH_FIELDS, 'authors', 'event', 'source'],
      plugins: [
        PluginType.Media,
        PluginType.Image,
        PluginType.Multimedia,
        PluginType.PageBreak,
        PluginType.Table,
        PluginType.Frame,
        PluginType.C2C,
      ],
    },
    editorialAlertAllowed: true,
    medias: {
      richTypes: RICH_MEDIA_TYPES,
    },
    internalLinks: [InternalLinkType.ARTICLE, InternalLinkType.RECIPE],
    personTagsAllowed: true,
    pushPlatforms: ['ios', 'android'],
    setInitialTitleAllowed: true,
    tagProviders: [TagType.Tag, TagType.Person],
    useFotoweb: true,
    enableNLFlash: true,
    isRecipe: true,
    enableAffilizPlugin: true,
  },
  FRGSD: {
    affiliate: true,
    isCerise: true,
    displayIATitle: true,
    draft: {
      richFields: [...BASE_RICH_FIELDS, 'authors', 'source'],
      plugins: [
        PluginType.Media,
        PluginType.Image,
        PluginType.Multimedia,
        PluginType.Frame,
        PluginType.C2C,
      ],
    },
    editorialAlertAllowed: true,
    medias: {
      richTypes: [
        MEDIA_TYPES.IMAGE,
        MEDIA_TYPES.EMBED,
        MEDIA_TYPES.ARTICLE_VIDEO,
        MEDIA_TYPES.FOTOWEB,
      ],
    },
    internalLinks: [InternalLinkType.ARTICLE],
    personTagsAllowed: true,
    tagProviders: [TagType.Tag, TagType.Person],
    useFotoweb: true,
  },
  FRGSG: {
    affiliate: true,
    isCerise: true,
    displayIATitle: true,
    draft: {
      richFields: [...BASE_RICH_FIELDS, 'authors', 'source'],
      plugins: [
        PluginType.Media,
        PluginType.Image,
        PluginType.Multimedia,
        PluginType.Frame,
        PluginType.C2C,
      ],
    },
    editorialAlertAllowed: true,
    medias: {
      richTypes: [
        MEDIA_TYPES.IMAGE,
        MEDIA_TYPES.EMBED,
        MEDIA_TYPES.ARTICLE_VIDEO,
        MEDIA_TYPES.FOTOWEB,
      ],
    },
    internalLinks: [InternalLinkType.ARTICLE],
    personTagsAllowed: true,
    tagProviders: [TagType.Tag, TagType.Person],
    useFotoweb: true,
  },
  FRGSL: {
    affiliate: true,
    isCerise: true,
    displayIATitle: true,
    draft: {
      richFields: [...BASE_RICH_FIELDS, 'authors', 'source'],
      plugins: [
        PluginType.Media,
        PluginType.Image,
        PluginType.Multimedia,
        PluginType.Frame,
        PluginType.C2C,
      ],
    },
    editorialAlertAllowed: true,
    medias: {
      richTypes: [
        MEDIA_TYPES.IMAGE,
        MEDIA_TYPES.EMBED,
        MEDIA_TYPES.ARTICLE_VIDEO,
        MEDIA_TYPES.FOTOWEB,
      ],
    },
    internalLinks: [InternalLinkType.ARTICLE],
    personTagsAllowed: true,
    tagProviders: [TagType.Tag, TagType.Person],
    useFotoweb: true,
  },
  FRGSS: {
    affiliate: true,
    isCerise: true,
    displayIATitle: true,
    draft: {
      richFields: [...BASE_RICH_FIELDS, 'authors', 'source'],
      plugins: [
        PluginType.Media,
        PluginType.Image,
        PluginType.Multimedia,
        PluginType.Frame,
        PluginType.C2C,
      ],
    },
    editorialAlertAllowed: true,
    medias: {
      richTypes: [
        MEDIA_TYPES.IMAGE,
        MEDIA_TYPES.EMBED,
        MEDIA_TYPES.ARTICLE_VIDEO,
        MEDIA_TYPES.FOTOWEB,
      ],
    },
    internalLinks: [InternalLinkType.ARTICLE],
    personTagsAllowed: true,
    tagProviders: [TagType.Tag, TagType.Person],
    useFotoweb: true,
  },
  FRGSV: {
    affiliate: true,
    isCerise: true,
    displayIATitle: true,
    draft: {
      richFields: [...BASE_RICH_FIELDS, 'authors', 'source'],
      plugins: [
        PluginType.Media,
        PluginType.Image,
        PluginType.Multimedia,
        PluginType.Frame,
        PluginType.C2C,
      ],
    },
    editorialAlertAllowed: true,
    medias: {
      richTypes: [
        MEDIA_TYPES.IMAGE,
        MEDIA_TYPES.EMBED,
        MEDIA_TYPES.ARTICLE_VIDEO,
        MEDIA_TYPES.FOTOWEB,
      ],
    },
    internalLinks: [InternalLinkType.ARTICLE],
    personTagsAllowed: true,
    tagProviders: [TagType.Tag, TagType.Person],
    useFotoweb: true,
  },
  FROMM: {
    affiliate: true,
    isCerise: true,
    displayIATitle: true,
    draft: {
      richFields: [...BASE_RICH_FIELDS, 'authors', 'source'],
      plugins: [
        PluginType.Media,
        PluginType.Image,
        PluginType.Multimedia,
        PluginType.Frame,
        PluginType.C2C,
      ],
    },
    editorialAlertAllowed: true,
    medias: {
      richTypes: [
        MEDIA_TYPES.IMAGE,
        MEDIA_TYPES.EMBED,
        MEDIA_TYPES.ARTICLE_VIDEO,
        MEDIA_TYPES.FOTOWEB,
      ],
    },
    internalLinks: [InternalLinkType.ARTICLE],
    personTagsAllowed: true,
    tagProviders: [TagType.Tag, TagType.Person],
    useFotoweb: true,
  },
  GEO: {
    affiliate: true,
    draft: {
      richFields: [...BASE_RICH_FIELDS, 'authors', 'source', 'guides'],
      plugins: [
        PluginType.Media,
        PluginType.Image,
        PluginType.Multimedia,
        PluginType.C2C,
        PluginType.Frame,
        PluginType.Table,
      ],
    },
    editorialAlertAllowed: true,
    medias: {
      richTypes: RICH_MEDIA_TYPES,
    },
    internalLinks: [InternalLinkType.ARTICLE],
    pushPlatforms: ['ios', 'android'],
    tagProviders: [TagType.Tag, TagType.Destination, TagType.Person],
    useFotoweb: true,
    enableNLFlash: true,
  },
  HBR: {
    draft: {
      richFields: [...BASE_RICH_FIELDS, 'authors'],
      plugins: [
        PluginType.Media,
        PluginType.Image,
        PluginType.Multimedia,
        PluginType.Frame,
        PluginType.Table,
      ],
    },
    editorialAlertAllowed: true,
    medias: {
      richTypes: RICH_MEDIA_TYPES,
    },
    pushPlatforms: ['ios', 'android'],
    tagProviders: [TagType.Tag],
    useFotoweb: true,
  },
  HBZ: {
    draft: {
      richFields: [
        ...BASE_RICH_FIELDS,

        'secondCategory',
        'authors',
        'source',
        'event',
      ],
      plugins: [
        PluginType.Media,
        PluginType.Image,
        PluginType.Multimedia,
        PluginType.PageBreak,
        PluginType.Frame,
        PluginType.Table,
      ],
    },
    editorialAlertAllowed: true,
    medias: {
      richTypes: [
        MEDIA_TYPES.IMAGE,
        MEDIA_TYPES.EMBED,
        MEDIA_TYPES.ARTICLE_VIDEO,
        MEDIA_TYPES.SLIDESHOW,
        MEDIA_TYPES.FOTOWEB,
      ],
    },
    personTagsAllowed: true,
    pushPlatforms: ['ios', 'android'],
    tagProviders: [TagType.Tag, TagType.Person],
    useFotoweb: true,
  },
  HCO: {
    draft: {
      richFields: [...BASE_RICH_FIELDS, 'authors', 'source'],
      plugins: [
        PluginType.Media,
        PluginType.Image,
        PluginType.Multimedia,
        PluginType.Frame,
        PluginType.C2C,
        PluginType.Table,
      ],
    },
    editorialAlertAllowed: true,
    medias: {
      richTypes: RICH_MEDIA_TYPES,
    },
    pushPlatforms: ['ios', 'android'],
    setInitialTitleAllowed: true,
    tagProviders: [TagType.Tag, TagType.Person],
    useFotoweb: true,
  },
  IDE: {
    draft: {
      richFields: [...BASE_RICH_FIELDS, 'authors', 'source'],
      plugins: [
        PluginType.Media,
        PluginType.Image,
        PluginType.Multimedia,
        PluginType.Frame,
        PluginType.C2C,
      ],
    },
    editorialAlertAllowed: true,
    medias: {
      richTypes: RICH_MEDIA_TYPES,
    },
    pushPlatforms: ['ios', 'android'],
    setInitialTitleAllowed: true,
    tagProviders: [TagType.Tag, TagType.Person],
    useFotoweb: true,
  },
  NEO: {
    draft: {
      richFields: [...BASE_RICH_FIELDS, 'authors', 'source'],
      plugins: [PluginType.Media, PluginType.Image, PluginType.Multimedia],
    },
    editorialAlertAllowed: true,
    medias: {
      richTypes: RICH_MEDIA_TYPES,
    },
    internalLinks: [InternalLinkType.ARTICLE],
    pushPlatforms: ['ios', 'android'],
    tagProviders: [TagType.Tag, TagType.Person],
    useFotoweb: true,
  },
  MLK: {
    draft: {
      richFields: [...BASE_RICH_FIELDS, 'authors', 'source'],
      plugins: [
        PluginType.Media,
        PluginType.Image,
        PluginType.Multimedia,
        PluginType.Frame,
        PluginType.C2C,
      ],
    },
    editorialAlertAllowed: true,
    medias: {
      richTypes: RICH_MEDIA_TYPES,
    },
    pushPlatforms: ['ios', 'android'],
    setInitialTitleAllowed: true,
    tagProviders: [TagType.Tag, TagType.Person],
    useFotoweb: true,
  },
  MVO: {
    draft: {
      richFields: [...BASE_RICH_FIELDS, 'authors', 'source'],
      plugins: [
        PluginType.Media,
        PluginType.Image,
        PluginType.Multimedia,
        PluginType.Frame,
        PluginType.C2C,
      ],
    },
    editorialAlertAllowed: true,
    medias: {
      richTypes: RICH_MEDIA_TYPES,
    },
    pushPlatforms: ['ios', 'android'],
    setInitialTitleAllowed: true,
    tagProviders: [TagType.Tag, TagType.Person],
    useFotoweb: true,
  },
  PRA: {
    draft: {
      richFields: [...BASE_RICH_FIELDS, 'authors', 'source'],
      plugins: [
        PluginType.Media,
        PluginType.Image,
        PluginType.Multimedia,
        PluginType.Frame,
        PluginType.C2C,
      ],
    },
    editorialAlertAllowed: true,
    medias: {
      richTypes: RICH_MEDIA_TYPES,
    },
    setInitialTitleAllowed: true,
    tagProviders: [TagType.Tag, TagType.Person],
    useFotoweb: true,
  },
  QUOI: {
    draft: {
      richFields: [...BASE_RICH_FIELDS, 'authors', 'source'],
      plugins: [
        PluginType.Media,
        PluginType.Image,
        PluginType.Multimedia,
        PluginType.Frame,
        PluginType.C2C,
        PluginType.Table,
      ],
    },
    editorialAlertAllowed: true,
    medias: {
      richTypes: RICH_MEDIA_TYPES,
    },
    pushPlatforms: ['ios', 'android'],
    setInitialTitleAllowed: true,
    tagProviders: [TagType.Tag, TagType.Person],
    useFotoweb: true,
  },
  SESH: {
    draft: {
      richFields: [...BASE_RICH_FIELDS, 'authors', 'source'],
      plugins: [
        PluginType.Media,
        PluginType.Image,
        PluginType.Multimedia,
        PluginType.Frame,
        PluginType.C2C,
      ],
    },
    editorialAlertAllowed: true,
    medias: {
      richTypes: RICH_MEDIA_TYPES,
    },
    pushPlatforms: ['ios', 'android'],
    setInitialTitleAllowed: true,
    tagProviders: [TagType.Tag, TagType.Person],
    useFotoweb: true,
  },
  SHGFAC: {
    affiliate: true,
    draft: {
      richFields: [...BASE_RICH_FIELDS, 'authors', 'event', 'source'],
      plugins: [
        PluginType.Media,
        PluginType.Image,
        PluginType.Multimedia,
        PluginType.PageBreak,
        PluginType.Table,
        PluginType.Frame,
        PluginType.C2C,
      ],
    },
    medias: {
      richTypes: RICH_MEDIA_TYPES,
    },
    internalLinks: [InternalLinkType.ARTICLE],
    tagProviders: [TagType.Tag, TagType.Person],
    useFotoweb: true,
  },
  SIM: {
    draft: {
      richFields: [...BASE_RICH_FIELDS, 'authors'],
      plugins: [PluginType.Media, PluginType.Image, PluginType.Multimedia],
    },
    medias: {
      richTypes: RICH_MEDIA_TYPES,
    },
    tagProviders: [TagType.Tag],
    useFotoweb: true,
  },
  TEL: {
    affiliate: true,
    draft: {
      richFields: [
        ...BASE_RICH_FIELDS,
        'secondCategory',
        'source',
        'authors',
        'event',
      ],
      plugins: [
        PluginType.Media,
        PluginType.Image,
        PluginType.Multimedia,
        PluginType.Table,
        PluginType.C2C,
      ],
    },
    editorialAlertAllowed: true,
    medias: {
      richTypes: RICH_MEDIA_TYPES,
    },
    mediaProgramsAllowed: true,
    internalLinks: [
      InternalLinkType.ARTICLE,
      InternalLinkType.PROGRAM,
      InternalLinkType.BROADCAST,
    ],
    personTagsAllowed: true,
    pushPlatforms: ['ios', 'android'],
    tagProviders: [
      TagType.Tag,
      TagType.Person,
      TagType.TvProgram,
      TagType.TvCollection,
    ],
    useFotoweb: true,
    enableNLFlash: true,
  },
  TESTBRAND: {
    draft: {
      richFields: [...BASE_RICH_FIELDS, 'secondCategory', 'authors'],
      plugins: [PluginType.Media, PluginType.Image, PluginType.Multimedia],
    },
    editorialAlertAllowed: true,
    medias: {
      richTypes: RICH_MEDIA_TYPES,
    },
    mediaProgramsAllowed: true,
    internalLinks: [
      InternalLinkType.ARTICLE,
      InternalLinkType.PROGRAM,
      InternalLinkType.BROADCAST,
    ],
    tagProviders: [
      TagType.Tag,
      TagType.Person,
      TagType.TvProgram,
      TagType.TvCollection,
      // TagType.TvBy,
    ],
    useFotoweb: true,
  },
  TGC: {
    displayIATitle: true,
    draft: {
      richFields: [...BASE_RICH_FIELDS, 'secondCategory', 'authors'],
      plugins: [
        PluginType.Media,
        PluginType.Image,
        PluginType.Multimedia,
        PluginType.Table,
        PluginType.C2C,
      ],
    },
    editorialAlertAllowed: true,
    medias: {
      richTypes: RICH_MEDIA_TYPES,
    },
    mediaProgramsAllowed: true,
    internalLinks: [
      InternalLinkType.ARTICLE,
      InternalLinkType.PROGRAM,
      InternalLinkType.BROADCAST,
    ],
    pushPlatforms: ['ios', 'android'],
    tagProviders: [
      TagType.Tag,
      TagType.Person,
      TagType.TvProgram,
      TagType.TvCollection,
    ],
    useFotoweb: true,
  },
  TGL: {
    draft: {
      richFields: [...BASE_RICH_FIELDS, 'authors', 'source'],
      plugins: [
        PluginType.Media,
        PluginType.Image,
        PluginType.Multimedia,
        PluginType.Frame,
        PluginType.C2C,
      ],
    },
    editorialAlertAllowed: true,
    medias: {
      richTypes: RICH_MEDIA_TYPES,
    },
    pushPlatforms: ['ios', 'android'],
    setInitialTitleAllowed: true,
    tagProviders: [TagType.Tag, TagType.Person],
    useFotoweb: true,
  },
  TLS: {
    draft: {
      richFields: [...BASE_RICH_FIELDS, 'secondCategory', 'authors'],
      plugins: [
        PluginType.Media,
        PluginType.Image,
        PluginType.Multimedia,
        PluginType.Table,
        PluginType.C2C,
      ],
    },
    editorialAlertAllowed: true,
    medias: {
      richTypes: RICH_MEDIA_TYPES,
    },
    mediaProgramsAllowed: true,
    internalLinks: [
      InternalLinkType.ARTICLE,
      InternalLinkType.PROGRAM,
      InternalLinkType.BROADCAST,
    ],
    pushPlatforms: ['ios', 'android'],
    tagProviders: [
      TagType.Tag,
      TagType.Person,
      TagType.TvProgram,
      TagType.TvCollection,
    ],
    useFotoweb: true,
    enableNLFlash: true,
  },
  TLZ: {
    draft: {
      richFields: [...BASE_RICH_FIELDS, 'authors', 'source'],
      plugins: [
        PluginType.Media,
        PluginType.Image,
        PluginType.Multimedia,
        PluginType.Frame,
        PluginType.C2C,
        PluginType.Table,
      ],
    },
    editorialAlertAllowed: true,
    medias: {
      richTypes: RICH_MEDIA_TYPES,
    },
    pushPlatforms: ['ios', 'android'],
    setInitialTitleAllowed: true,
    tagProviders: [TagType.Tag, TagType.Person],
    useFotoweb: true,
  },
  TMA: {
    draft: {
      richFields: [...BASE_RICH_FIELDS, 'authors', 'source'],
      plugins: [
        PluginType.Media,
        PluginType.Image,
        PluginType.Multimedia,
        PluginType.Frame,
        PluginType.C2C,
      ],
    },
    editorialAlertAllowed: true,
    medias: {
      richTypes: RICH_MEDIA_TYPES,
    },
    pushPlatforms: ['ios', 'android'],
    setInitialTitleAllowed: true,
    tagProviders: [TagType.Tag, TagType.Person],
    useFotoweb: true,
  },
  TVP: {
    draft: {
      richFields: [...BASE_RICH_FIELDS, 'authors', 'source'],
      plugins: [
        PluginType.Media,
        PluginType.Image,
        PluginType.Multimedia,
        PluginType.Frame,
        PluginType.C2C,
        PluginType.Table,
      ],
    },
    editorialAlertAllowed: true,
    medias: {
      richTypes: RICH_MEDIA_TYPES,
    },
    pushPlatforms: ['ios', 'android'],
    setInitialTitleAllowed: true,
    tagProviders: [TagType.Tag, TagType.Person],
    useFotoweb: true,
  },
  UKGTS: {
    isCerise: true,
    draft: {
      richFields: [...BASE_RICH_FIELDS, 'authors', 'source'],
      plugins: [
        PluginType.Media,
        PluginType.Image,
        PluginType.Multimedia,
        PluginType.Frame,
        PluginType.C2C,
      ],
    },
    editorialAlertAllowed: true,
    medias: {
      richTypes: [
        MEDIA_TYPES.IMAGE,
        MEDIA_TYPES.EMBED,
        MEDIA_TYPES.ARTICLE_VIDEO,
        MEDIA_TYPES.FOTOWEB,
      ],
    },
    internalLinks: [InternalLinkType.ARTICLE],
    tagProviders: [TagType.Tag],
    useFotoweb: true,
  },
  UKOMM: {
    isCerise: true,
    draft: {
      richFields: [...BASE_RICH_FIELDS, 'authors', 'source'],
      plugins: [
        PluginType.Media,
        PluginType.Image,
        PluginType.Multimedia,
        PluginType.Frame,
        PluginType.C2C,
      ],
    },
    editorialAlertAllowed: true,
    medias: {
      richTypes: [
        MEDIA_TYPES.IMAGE,
        MEDIA_TYPES.EMBED,
        MEDIA_TYPES.ARTICLE_VIDEO,
        MEDIA_TYPES.FOTOWEB,
      ],
    },
    internalLinks: [InternalLinkType.ARTICLE],
    tagProviders: [TagType.Tag],
    useFotoweb: true,
  },
  VOI: {
    affiliate: true,
    draft: {
      richFields: [...BASE_RICH_FIELDS, 'secondCategory', 'authors', 'event'],
      plugins: [
        PluginType.Media,
        PluginType.Image,
        PluginType.Multimedia,
        PluginType.Frame,
        PluginType.C2C,
        PluginType.Table,
      ],
    },
    editorialAlertAllowed: true,
    medias: {
      richTypes: RICH_MEDIA_TYPES,
    },
    internalLinks: [InternalLinkType.ARTICLE],
    personTagsAllowed: true,
    pushPlatforms: ['ios', 'android'],
    tagProviders: [TagType.Tag, TagType.Person],
    useFotoweb: true,
    enableNLFlash: true,
    enableAffilizPlugin: true,
  },
  DEFAULT: {
    affiliate: false,
    draft: {
      richFields: [],
      plugins: [],
    },
    editorialAlertAllowed: false,
    medias: {
      richTypes: [],
    },
    internalLinks: [],
    personTagsAllowed: false,
    pushPlatforms: [],
    tagProviders: [],
    useFotoweb: false,
    enableNLFlash: false,
    enableAffilizPlugin: false,
  },
};

export function getBrandOptions(brandKey: BrandKey) {
  return brandsOptions[brandKey as BrandKey] ?? brandsOptions.DEFAULT;
}

export const getTitleFromBrandKey = (brandKey: keyof typeof BrandKey | null) =>
  brandKey && brandTitleByKey[brandKey] ? brandTitleByKey[brandKey] : brandKey;

export const getBrandkeys = (predicate: (options: BrandOptions) => boolean) =>
  Object.entries(brandsOptions)
    .filter(([, options]) => predicate(options))
    .map(([key]) => key as BrandKey);
